<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import TermosModal from './components/TermosModal.vue'
import { useCadastrarStore } from './store/useCadastrarStore'
import * as rules from '@/validators/cdf-rules'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import logo from '@images/logo-right.png'
import logoDark from '@images/logo-right-white.png'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'

const themeLogo = useGenerateImageVariant(logo, logoDark)
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'

const isPasswordVisible = ref(false)
const showTermosModal = ref(false)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const store = useCadastrarStore()

const {
  form,
  data,
  loading,
} = storeToRefs(store)

const {
  createAccount,
} = store

const openTermosModal = () => {
  showTermosModal.value = true
}

const handleTermosAccepted = () => {
  data.value.termos = true
}

const isTermosAccepted = computed(() => data.value.termos === true)

const userTypeForTermos = computed(() => {
  return data.value.roles as 'fornecedor' | 'espaco-eventos' | 'organizador'
})

const roleInfoMap: Record<string, { icon: string; color: string; title: string; description: string }> = {
  'fornecedor': {
    icon: 'tabler-building-store',
    color: 'primary',
    title: 'Para empresas que oferecem produtos e serviços para eventos',
    description: 'Cadastre-se como fornecedor se você oferece buffet, decoração, som, iluminação, fotografia ou qualquer outro serviço/produto para eventos. Você poderá divulgar seu catálogo e receber solicitações de organizadores.',
  },
  'espaco-eventos': {
    icon: 'tabler-map-pin',
    color: 'info',
    title: 'Para quem possui espaços disponíveis para eventos',
    description: 'Cadastre-se aqui se você é proprietário ou administrador de salões de festas, chácaras, espaços corporativos ou qualquer local disponível para locação de eventos.',
  },
  'organizador': {
    icon: 'tabler-calendar-event',
    color: 'success',
    title: 'Para quem organiza e produz eventos',
    description: 'Cadastre-se como organizador se você planeja e coordena eventos como casamentos, formaturas, congressos ou eventos corporativos. Encontre fornecedores e espaços ideais para suas produções.',
  },
}
</script>

<template>
  <div class="justify-center auth-wrapper d-flex align-center pa-4">
    <div class="position-relative my-sm-16 w-100" style="max-width: 600px;">
      <!-- 👉 Top shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <!-- 👉 Bottom shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <!-- 👉 Auth card -->
      <VCard
        class="auth-card"
        width="100%"
        :class="$vuetify.display.smAndUp ? 'pa-6' : 'pa-3'"
      >
        <VCardItem class="justify-center pa-0">
          <VCardTitle>
            <img
              :src="themeLogo"
              height="80"
              alt="Connect Eventos"
            >
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <h5 class="mb-1 text-center text-h6 text-primary">
            Cadastre-se em nosso sistema
          </h5>
        </VCardText>

        <VCardText>
          <VTabs
            v-model="data.roles"
            fixed-tabs
            class="mb-4"
          >
            <VTab
              class="text-caption text-sm-body-2 font-weight-bold"
              value="fornecedor"
            >
              Fornecedor
            </VTab>
            <VTab
              class="text-caption text-sm-body-2 font-weight-bold"
              value="espaco-eventos"
            >
              Espaço p/ Eventos
            </VTab>
            <VTab
              class="text-caption text-sm-body-2 font-weight-bold"
              value="organizador"
            >
              Organizador
            </VTab>
          </VTabs>

          <!-- Card de apoio contextual -->
          <VCard
            variant="tonal"
            :color="roleInfoMap[data.roles]?.color || 'primary'"
            class="mb-5 role-info-card"
          >
            <VCardText class="d-flex align-start gap-3 pa-4">
              <VIcon
                :icon="roleInfoMap[data.roles]?.icon || 'tabler-info-circle'"
                size="28"
                :color="roleInfoMap[data.roles]?.color || 'primary'"
                class="mt-1 flex-shrink-0"
              />
              <div>
                <p class="text-subtitle-2 font-weight-bold mb-1" style="line-height: 1.4;">
                  {{ roleInfoMap[data.roles]?.title }}
                </p>
                <p class="text-caption mb-0" style="line-height: 1.5; opacity: 0.85;">
                  {{ roleInfoMap[data.roles]?.description }}
                </p>
              </div>
            </VCardText>
          </VCard>

          <VForm ref="form">
            <VRow>
              <!-- Razão Social -->
              <VCol
                v-if="data.roles === 'fornecedor' || data.roles === 'espaco-eventos'"
                cols="12"
                class="py-1"
              >
                <AppTextField
                  v-model="data.nome_fantasia"
                  :rules="[rules.requiredValidator]"
                  label="Nome Fantasia"
                  placeholder="Digite a Nome Fantasia"
                />
              </VCol>

              <!-- CNPJ -->
              <VCol
                v-if="data.roles === 'fornecedor' || data.roles === 'espaco-eventos'"
                cols="12"
                class="py-1"
              >
                <AppTextField
                  v-model="data.cnpj"
                  v-mask="data.cnpj.length > 14 ? '##.###.###/####-##' : '###.###.###-##'"
                  :rules="[rules.requiredValidator]"
                  label="CPF/CNPJ"
                  placeholder="Ex: 00.000.000-00 ou 00.000.000/0000-00"
                />
              </VCol>

              <VDivider
                v-if="data.roles === 'fornecedor' || data.roles === 'espaco-eventos'"
                class="mx-3 my-4"
              />

              <!-- Nome Completo -->
              <VCol
                cols="12"
                class="py-1"
              >
                <AppTextField
                  v-model="data.nome_completo"
                  :rules="[rules.requiredValidator]"
                  label="Nome Completo"
                  placeholder="Digite seu nome completo"
                />
              </VCol>

              <!-- email -->
              <VCol
                cols="12"
                class="py-1"
              >
                <CDFTextField
                  v-model="data.email"
                  :rules="[rules.requiredValidator, rules.emailValidator]"
                  label="Email"
                  type="email"
                  placeholder="johndoe@email.com"
                />
              </VCol>

              <!-- password -->
              <VCol
                cols="12"
                class="py-1"
              >
                <CDFTextField
                  v-model="data.password"
                  :rules="[rules.requiredValidator]"
                  label="Senha"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <VAlert
                  v-if="!isTermosAccepted"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="my-4"
                >
                  <template #prepend>
                    <VIcon icon="tabler-info-circle" />
                  </template>
                  <div class="gap-2 d-flex flex-column">
                    <span class="text-sm">
                      Para continuar, você precisa ler e aceitar nossos Termos de Uso e Política de Privacidade.
                    </span>
                    <VBtn
                      color="primary"
                      variant="tonal"
                      size="small"
                      @click="openTermosModal"
                    >
                      <VIcon
                        icon="tabler-file-text"
                        start
                      />
                      Ler Termos e Política
                    </VBtn>
                  </div>
                </VAlert>

                <VAlert
                  v-else
                  type="success"
                  variant="tonal"
                  density="compact"
                  class="my-4"
                >
                  <template #prepend>
                    <VIcon icon="tabler-check" />
                  </template>
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-sm font-weight-medium">
                      Você leu e aceitou os Termos de Uso e Política de Privacidade
                    </span>
                    <VBtn
                      color="success"
                      variant="text"
                      size="small"
                      @click="openTermosModal"
                    >
                      Reler
                    </VBtn>
                  </div>
                </VAlert>

                <VBtn
                  :loading="loading"
                  :disabled="loading || !isTermosAccepted"
                  block
                  @click="createAccount"
                >
                  Cadastrar
                </VBtn>
              </VCol>

              <!-- create account -->
              <VCol
                cols="12"
                class="text-base text-center"
              >
                <span class="d-inline-block">Já tem uma conta?</span>
                <RouterLink
                  class="text-primary ms-1 d-inline-block"
                  :to="{ name: 'admin-auth-login' }"
                >
                  Em vez disso, faça login
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>

    <!-- Modal de Termos e Política -->
    <TermosModal
      v-model="showTermosModal"
      :user-type="userTypeForTermos"
      @accepted="handleTermosAccepted"
    />
  </div>
</template>
